import { toPng } from "html-to-image";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { storage, db } from "./firebase-config";

export const SITE_URL = "https://trustgame.co.uk";

// Upload a DOM node as PNG to Firebase Storage, write metadata to Firestore,
// and return a shareable URL (https://trustgame.co.uk/share/{id}).
// That URL is served by the shareog Cloud Function which returns an HTML page
// with Open Graph meta tags so social platforms show a rich preview card.
export async function createShareCard(node, { title = "trust game", description = "play at trustgame.co.uk" } = {}) {
  if (!node) throw new Error("nothing to share");
  const dataUrl = await toPng(node, { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff" });
  const blob = await (await fetch(dataUrl)).blob();
  const id = crypto.randomUUID();
  const storageRef = ref(storage, `shared/${id}.png`);
  await uploadBytes(storageRef, blob, { contentType: "image/png" });
  const imageUrl = await getDownloadURL(storageRef);
  await setDoc(doc(db, "shareCards", id), { imageUrl, title, description, createdAt: serverTimestamp() });
  return `${SITE_URL}/share/${id}`;
}

// For platform buttons (twitter/facebook/whatsapp):
//   Creates an OG share card, then opens the platform's compose URL with it.
//   Social platforms fetch that URL, read the og:image meta tag, and display
//   the card as a rich preview — no file download involved.
//
// For platform = "image" (generic save):
//   Mobile (iOS/Android): uses Web Share API to share the PNG file directly.
//   Desktop: downloads the PNG.
export async function shareImageToPlatform(node, { platform = "image", text = "", title = "trust game", description = "", url = SITE_URL } = {}) {
  if (!node) return { ok: false, error: "nothing to share" };

  if (["twitter", "facebook", "whatsapp"].includes(platform)) {
    try {
      const shareUrl = await createShareCard(node, { title, description: description || text || title });
      const platformUrls = {
        twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(text ? `${text}\n${shareUrl}` : shareUrl)}`,
      };
      window.open(platformUrls[platform], "_blank", "noopener,noreferrer");
      return { ok: true, shareUrl };
    } catch (error) {
      console.error("Share failed:", error);
      return { ok: false, error: error.message || "failed to create share card" };
    }
  }

  // Generic "image" button: native share on real mobile, download on desktop
  try {
    const dataUrl = await toPng(node, { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff" });
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], "trust-game.png", { type: "image/png" });
    const isMobilePlatform = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobilePlatform && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "trust game", text: text ? `${text}\n${url}` : url });
      return { ok: true, method: "native" };
    }
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "trust-game.png";
    a.click();
    return { ok: true, method: "download" };
  } catch (error) {
    if (error.name === "AbortError") return { ok: true, method: "cancelled" };
    console.error("Share failed:", error);
    return { ok: false, error: error.message || "failed to share" };
  }
}

// Kept for backwards-compatibility — Admin's StatsViewer still calls this.
export async function shareCardAsImage(node, { text = "", url = SITE_URL } = {}) {
  return shareImageToPlatform(node, { platform: "image", text, url });
}
